/*
 * Broadcast Theme
 *
 * Use this file to add custom Javascript to Broadcast.  Keeping your custom
 * Javascript in this fill will make it easier to update Broadcast. In order
 * to use this file you will need to open layout/theme.liquid and uncomment
 * the custom.js script import line near the bottom of the file.
 */

(function () {
  // Add custom code below this line

  const WISHLIST_STORAGE_KEY = "casavelluti:wishlist";
  const SMART_STICKY_ATC_SELECTOR = "[data-mobile-sticky-atc]";
  const ZOOM_GALLERY_SELECTOR = ".pswp";
  let accessibilityObserver = null;
  let accessibilityRoots = new Set();
  let accessibilityUpdateTimer = null;

  const syncZoomGalleryActions = (productHandle) => {
    const gallery = document.querySelector(ZOOM_GALLERY_SELECTOR);
    const wishlistButton = gallery?.querySelector("[data-zoom-wishlist]");
    if (!wishlistButton) return;

    wishlistButton.dataset.productHandle = productHandle || "";
    const wishlist = getWishlist();
    const isActive = Boolean(productHandle && wishlist.includes(productHandle));
    wishlistButton.classList.toggle("is-active", isActive);
    wishlistButton.setAttribute("aria-pressed", isActive ? "true" : "false");
    wishlistButton.setAttribute(
      "aria-label",
      isActive ? "Remove from Wishlist" : "Add to Wishlist",
    );
  };

  const initMobileZoomActions = () => {
    const gallery = document.querySelector(ZOOM_GALLERY_SELECTOR);
    if (!gallery || gallery.dataset.mobileActionsReady) return;

    gallery.dataset.mobileActionsReady = "true";
    document.addEventListener("click", (event) => {
      const image = event.target.closest?.("[data-zoom-image]");
      if (!image) return;

      const productSection = image.closest('[data-section-type="product"]');
      syncZoomGalleryActions(productSection?.dataset.productHandle);
    });
  };

  class SmartStickyAddToCart {
    constructor(container) {
      this.container = container;
      this.productSection = container.closest('[data-section-type="product"]');
      this.originalButton =
        this.productSection?.querySelector("[data-add-to-cart]");
      this.productForm = this.productSection?.querySelector(
        "[data-product-form]",
      );
      this.price = container.querySelector("[data-mobile-sticky-atc-price]");
      this.comparePrice = container.querySelector(
        "[data-mobile-sticky-atc-compare]",
      );
      this.button = container.querySelector("[data-mobile-sticky-atc-button]");
      this.buttonText = container.querySelector(
        "[data-mobile-sticky-atc-text]",
      );
      this.loader = container.querySelector("[data-mobile-sticky-atc-loader]");
      this.pending = false;
      this.successTimer = null;
      this.overlayObserver = null;

      if (
        !this.productSection ||
        !this.originalButton ||
        !this.productForm ||
        !this.button
      ) {
        return;
      }

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onStickyClick = this.onStickyClick.bind(this);
      this.onProductAdded = this.onProductAdded.bind(this);
      this.onProductError = this.onProductError.bind(this);
      this.updateOverlayState = this.updateOverlayState.bind(this);

      this.button.addEventListener("click", this.onStickyClick);
      this.productSection.addEventListener(
        "theme:variant:change",
        this.onVariantChange,
      );
      document.addEventListener("theme:product:added", this.onProductAdded);
      document.addEventListener("theme:product:add-error", this.onProductError);
      document.addEventListener(
        "theme:cart-drawer:open",
        this.updateOverlayState,
      );
      document.addEventListener(
        "theme:cart-drawer:close",
        this.updateOverlayState,
      );
      document.addEventListener("theme:modal:open", this.updateOverlayState);
      document.addEventListener("theme:modal:close", this.updateOverlayState);

      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.setVisibility(!entry.isIntersecting);
        },
        { threshold: 0.01 },
      );
      this.intersectionObserver.observe(this.originalButton);

      if (window.MutationObserver) {
        this.overlayObserver = new MutationObserver(this.updateOverlayState);
        this.overlayObserver.observe(document.body, {
          attributes: true,
          subtree: true,
          attributeFilter: ["class", "open"],
        });
      }

      document.body.classList.add("has-smart-sticky-atc");
      this.syncVariantState();
      this.updateOverlayState();
    }

    destroy() {
      this.button?.removeEventListener("click", this.onStickyClick);
      this.productSection?.removeEventListener(
        "theme:variant:change",
        this.onVariantChange,
      );
      document.removeEventListener("theme:product:added", this.onProductAdded);
      document.removeEventListener(
        "theme:product:add-error",
        this.onProductError,
      );
      document.removeEventListener(
        "theme:cart-drawer:open",
        this.updateOverlayState,
      );
      document.removeEventListener(
        "theme:cart-drawer:close",
        this.updateOverlayState,
      );
      document.removeEventListener("theme:modal:open", this.updateOverlayState);
      document.removeEventListener(
        "theme:modal:close",
        this.updateOverlayState,
      );
      this.intersectionObserver?.disconnect();
      this.overlayObserver?.disconnect();
      window.clearTimeout(this.successTimer);

      if (!document.querySelector(SMART_STICKY_ATC_SELECTOR)) {
        document.body.classList.remove("has-smart-sticky-atc");
        document.body.classList.remove("is-smart-sticky-atc-visible");
      }
    }

    setVisibility(visible) {
      this.container.classList.toggle("is-atc-visible", visible);
      document.body.classList.toggle("is-smart-sticky-atc-visible", visible);
      this.updateOverlayState();
    }

    isOverlayOpen() {
      return Boolean(
        document.querySelector(
          ".drawer.is-open, dialog[open][data-scroll-lock-required], [data-tracking-consent][open]",
        ),
      );
    }

    updateOverlayState() {
      if (!this.button) return;

      const hidden = this.isOverlayOpen();
      this.container.classList.toggle("is-overlay-hidden", hidden);
      this.container.setAttribute("aria-hidden", hidden ? "true" : "false");
    }

    getOptionState() {
      const selectors = this.productSection.querySelector(
        "[data-product-variants]",
      );
      if (!selectors) return { missing: [], firstMissing: null };

      const options = Array.from(
        selectors.querySelectorAll("[data-option-position]"),
      );
      const sizeOptions = options.filter((option) => {
        const name =
          option.querySelector(".radio__legend__option-name")?.textContent ||
          "";
        return /size|top|bottom/i.test(name);
      });
      const relevantOptions = sizeOptions.length ? sizeOptions : options;
      const missing = relevantOptions.filter((option) => {
        const selected =
          option.querySelector("input[data-single-option-selector]:checked") ||
          option.querySelector("[data-popout-input]");
        return !selected?.value?.trim();
      });

      return { missing, firstMissing: missing[0] || null };
    }

    getMissingSizeLabel(missing) {
      const names = missing.map(
        (option) =>
          option
            .querySelector(".radio__legend__option-name")
            ?.textContent.trim()
            .toLowerCase() || "",
      );
      const hasTop = names.some((name) => name.includes("top"));
      const hasBottom = names.some((name) => name.includes("bottom"));

      if (hasTop && hasBottom) return "CHOOSE YOUR SIZE";
      if (hasTop) return "CHOOSE TOP SIZE";
      if (hasBottom) return "CHOOSE BOTTOM SIZE";
      return "CHOOSE YOUR SIZE";
    }

    syncVariantState() {
      const optionState = this.getOptionState();
      const priceElement = this.productSection.querySelector(
        "[data-price-wrapper] [data-product-price]",
      );
      const currentPrice = priceElement?.textContent.trim();
      const compareElement = this.productSection.querySelector(
        "[data-price-wrapper] [data-compare-price]",
      );
      const compareText = compareElement?.textContent.trim();
      const onSale = Boolean(
        compareElement &&
          !compareElement.classList.contains("hidden") &&
          compareText,
      );
      const originalWrapper = this.originalButton.closest(
        "[data-form-wrapper]",
      );
      const unavailable = originalWrapper?.classList.contains(
        "variant--unavailable",
      );
      const soldOut =
        originalWrapper?.classList.contains("variant--soldout") ||
        this.originalButton.disabled;

      if (currentPrice && this.price) this.price.textContent = currentPrice;

      if (this.comparePrice) {
        if (onSale) {
          this.comparePrice.textContent = compareText;
          this.comparePrice.classList.remove("hidden");
        } else {
          this.comparePrice.textContent = "";
          this.comparePrice.classList.add("hidden");
        }
      }

      if (optionState.missing.length) {
        this.setState(
          this.getMissingSizeLabel(optionState.missing),
          true,
          true,
        );
      } else if (unavailable) {
        this.setState("UNAVAILABLE", true);
      } else if (soldOut) {
        this.setState("SOLD OUT", true);
      } else {
        this.setState(
          this.originalButton
            .querySelector("[data-add-to-cart-text]")
            ?.textContent.trim() || "ADD TO CART",
          false,
        );
      }
    }

    setState(text, disabled, interactive = false) {
      if (this.pending) return;
      this.buttonText.textContent = text;
      this.button.disabled = disabled && !interactive;
      this.button.setAttribute("aria-disabled", disabled ? "true" : "false");
      this.button.setAttribute("aria-busy", "false");
    }

    onVariantChange() {
      this.syncVariantState();
    }

    onStickyClick() {
      if (this.pending) return;

      const optionState = this.getOptionState();
      if (optionState.missing.length) {
        optionState.firstMissing?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        optionState.firstMissing?.classList.add("casavelluti-size-highlight");
        window.setTimeout(
          () =>
            optionState.firstMissing?.classList.remove(
              "casavelluti-size-highlight",
            ),
          700,
        );
        return;
      }

      if (this.originalButton.disabled) return;

      this.pending = true;
      this.button.disabled = true;
      this.button.setAttribute("aria-disabled", "true");
      this.button.setAttribute("aria-busy", "true");
      this.button.classList.add("is-loading");
      this.buttonText.textContent = "ADDING...";
      this.originalButton.click();
    }

    onProductAdded() {
      if (!this.pending) return;

      this.pending = false;
      this.button.classList.remove("is-loading");
      this.button.setAttribute("aria-busy", "false");
      this.button.classList.add("is-added");
      this.buttonText.textContent = "✓ ADDED TO CART";
      this.successTimer = window.setTimeout(() => {
        this.button.classList.remove("is-added");
        this.syncVariantState();
      }, 1200);
    }

    onProductError(event) {
      if (!this.pending || event.detail?.button !== this.originalButton) return;

      this.pending = false;
      this.button.classList.remove("is-loading");
      this.button.setAttribute("aria-busy", "false");
      this.syncVariantState();
    }
  }

  const initSmartStickyAtc = (root = document) => {
    root.querySelectorAll?.(SMART_STICKY_ATC_SELECTOR).forEach((container) => {
      if (!container.dataset.smartStickyReady) {
        container.dataset.smartStickyReady = "true";
        container.smartStickyInstance = new SmartStickyAddToCart(container);
      }
    });
  };

  // Helper functions for LocalStorage
  const getWishlist = () => {
    try {
      const stored = localStorage.getItem(WISHLIST_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error("Error reading wishlist from localStorage", e);
      return [];
    }
  };

  const saveWishlist = (wishlist) => {
    try {
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlist));
      // Dispatch custom event to notify all components
      document.dispatchEvent(
        new CustomEvent("wishlist:updated", { detail: wishlist }),
      );
    } catch (e) {
      console.error("Error saving wishlist to localStorage", e);
    }
  };

  const toggleWishlistItem = (handle) => {
    let wishlist = getWishlist();
    const index = wishlist.indexOf(handle);
    if (index > -1) {
      wishlist.splice(index, 1);
    } else {
      wishlist.push(handle);
    }
    saveWishlist(wishlist);
    return index === -1; // returns true if added, false if removed
  };

  // Update all UI elements based on current wishlist state
  const updateUI = (wishlist = getWishlist()) => {
    const wishlistItems = Array.isArray(wishlist) ? wishlist : getWishlist();
    const count = wishlistItems.length;

    document.querySelectorAll("[data-wishlist-count]").forEach((counter) => {
      counter.textContent = count > 99 ? "99+" : count;
      counter.classList.toggle("is-hidden", count === 0);
      counter.setAttribute("aria-hidden", count === 0 ? "true" : "false");
    });

    document.querySelectorAll("[data-wishlist-button]").forEach((button) => {
      const handle =
        button.dataset.productHandle ||
        button.getAttribute("data-product-handle");
      if (!handle) return;

      const isActive = wishlistItems.includes(handle);
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
      button.setAttribute(
        "aria-label",
        isActive ? "Remove from Wishlist" : "Add to Wishlist",
      );
    });
  };

  // Init page event listeners
  const initEventListeners = () => {
    // Event delegation for wishlist toggle buttons
    document.addEventListener("click", (e) => {
      const button = e.target.closest("[data-wishlist-button]");
      if (!button) return;

      e.preventDefault();
      e.stopPropagation();

      const handle = button.getAttribute("data-product-handle");
      if (!handle) return;

      const isAdded = toggleWishlistItem(handle);

      // If we are on the wishlist page and an item is removed, animate and remove it
      const wishlistPage = document.querySelector(
        '[data-section-type="wishlist-page"]',
      );
      if (wishlistPage && !isAdded) {
        const productCard =
          button.closest(".grid-item") ||
          Array.from(
            wishlistPage.querySelectorAll(".wishlist-grid-wrapper .grid-item"),
          ).find((card) =>
            Array.from(card.querySelectorAll("[data-wishlist-button]")).some(
              (cardButton) =>
                cardButton.getAttribute("data-product-handle") === handle,
            ),
          );

        if (productCard) {
          productCard.style.transition =
            "opacity 0.3s ease, transform 0.3s ease";
          productCard.style.opacity = "0";
          productCard.style.transform = "scale(0.9)";
          setTimeout(() => {
            productCard.remove();

            // Check if wishlist page is now empty
            const currentWishlist = getWishlist();
            const section = document.querySelector(".wishlist-page-section");

            if (section && currentWishlist.length === 0) {
              setWishlistPageState(section, "is-empty");

              const skeletons = document.getElementById("WishlistSkeletons");
              const gridWrapper = document.getElementById(
                "WishlistGridWrapper",
              );

              if (skeletons) skeletons.style.display = "none";
              if (gridWrapper) gridWrapper.style.display = "none";
            }
          }, 300);
        }
      }
    });

    // Listen to custom wishlist update event
    document.addEventListener("wishlist:updated", (e) => {
      updateUI(e.detail);
      const gallery = document.querySelector(ZOOM_GALLERY_SELECTOR);
      const wishlistButton = gallery?.querySelector("[data-zoom-wishlist]");
      if (wishlistButton?.dataset.productHandle) {
        syncZoomGalleryActions(wishlistButton.dataset.productHandle);
      }
    });
  };
  const setWishlistPageState = (section, state) => {
    if (!section) return;

    section.classList.remove(
      "is-empty",
      "has-items",
      "is-loading",
      "is-loaded",
    );

    if (state) {
      section.classList.add(state);
    }
  };

  // Handle loading products on the Wishlist page
  // Handle loading products on the Wishlist page
  const loadWishlistPage = async () => {
    const section = document.querySelector(".wishlist-page-section");
    if (!section) return;

    const gridWrapper = document.getElementById("WishlistGridWrapper");
    const gridContainer = document.getElementById("WishlistGridContainer");
    const wishlist = getWishlist();

    if (gridContainer) {
      gridContainer.innerHTML = "";
    }

    // Empty wishlist state
    if (wishlist.length === 0) {
      section.classList.remove("is-loading", "has-items", "is-loaded");
      section.classList.add("is-empty");

      if (gridWrapper) {
        gridWrapper.style.display = "none";
      }

      updateUI(wishlist);
      return;
    }

    // Loading without skeleton/mockups
    section.classList.remove("is-empty", "has-items", "is-loaded");
    section.classList.add("is-loading");

    if (gridWrapper) {
      gridWrapper.style.display = "block";
    }

    const validHandles = [];

    const productRequests = wishlist.map((handle, index) => {
      return fetch(
        `${window.theme.routes.root}products/${handle}?section_id=api-product-grid-item`,
      )
        .then((response) => {
          if (!response.ok) throw new Error("Product not found");
          return response.text();
        })
        .then((html) => {
          const tempDiv = document.createElement("div");

          const aosDelay = index * 100;
          const aosAnchor = "#WishlistGridContainer";

          let productHtml = html;

          productHtml = productHtml.includes("||itemAnimationDelay||")
            ? productHtml.replaceAll("||itemAnimationDelay||", aosDelay)
            : productHtml;

          productHtml = productHtml.includes("||itemAnimationAnchor||")
            ? productHtml.replaceAll("||itemAnimationAnchor||", aosAnchor)
            : productHtml;

          tempDiv.innerHTML = productHtml;
          tempDiv.querySelectorAll("[data-aos]").forEach((element) => {
            element.classList.add("aos-animate");
          });

          const content = tempDiv.querySelector("[data-api-content]");

          if (content && gridContainer) {
            gridContainer.insertAdjacentHTML("beforeend", content.innerHTML);
            validHandles.push(handle);
          }
        })
        .catch((error) => {
          console.error(`Error loading product card for ${handle}:`, error);
        });
    });

    await Promise.all(productRequests);

    // Remove invalid/deleted products from localStorage
    saveWishlist(validHandles);

    if (validHandles.length === 0) {
      section.classList.remove("is-loading", "has-items", "is-loaded");
      section.classList.add("is-empty");

      if (gridWrapper) {
        gridWrapper.style.display = "none";
      }

      if (gridContainer) {
        gridContainer.innerHTML = "";
      }

      updateUI(validHandles);
      return;
    }

    section.classList.remove("is-empty", "is-loading");
    section.classList.add("has-items", "is-loaded");

    if (gridWrapper) {
      gridWrapper.style.display = "block";
    }

    updateUI(validHandles);
  };

  // Accessibility Helpers
  const ensureIframeTitles = (root = document) => {
    const frames = [];

    if (
      root.nodeType === Node.ELEMENT_NODE &&
      root.matches("iframe:not([title]), frame:not([title])")
    ) {
      frames.push(root);
    }

    if (root.querySelectorAll) {
      frames.push(
        ...root.querySelectorAll("iframe:not([title]), frame:not([title])"),
      );
    }

    frames.forEach((frame) => {
      const frameId = frame.getAttribute("id") || "";
      const frameName = frame.getAttribute("name") || "";
      let frameTitle = "Preview Frame";
      if (frameId === "PBarNextFrame" || frameId.includes("PBar")) {
        frameTitle = "Shopify Preview Bar";
      } else if (frameId) {
        frameTitle = frameId.replace(/[-_]/g, " ");
      } else if (frameName) {
        frameTitle = frameName.replace(/[-_]/g, " ");
      }
      frame.setAttribute("title", frameTitle);
    });
  };

  const clarifyDisclaimerLinks = (root = document) => {
    const privacyLinks = [];
    const termsLinks = [];

    if (root.nodeType === Node.ELEMENT_NODE) {
      if (root.matches('a[href*="hcaptcha.com/privacy"]'))
        privacyLinks.push(root);
      if (root.matches('a[href*="hcaptcha.com/terms"]')) termsLinks.push(root);
    }

    if (root.querySelectorAll) {
      privacyLinks.push(
        ...root.querySelectorAll('a[href*="hcaptcha.com/privacy"]'),
      );
      termsLinks.push(
        ...root.querySelectorAll('a[href*="hcaptcha.com/terms"]'),
      );
    }

    privacyLinks.forEach((a) => {
      a.setAttribute(
        "aria-label",
        "hCaptcha Privacy Policy (opens external site)",
      );
    });
    termsLinks.forEach((a) => {
      a.setAttribute(
        "aria-label",
        "hCaptcha Terms of Service (opens external site)",
      );
    });
  };

  const applyAccessibilityFixes = (root) => {
    ensureIframeTitles(root);
    clarifyDisclaimerLinks(root);
  };

  const flushAccessibilityRoots = () => {
    accessibilityUpdateTimer = null;
    accessibilityRoots.forEach((root) => applyAccessibilityFixes(root));
    accessibilityRoots = new Set();
    updateUI();
  };

  const initAccessibilityFixes = () => {
    applyAccessibilityFixes(document);

    if (window.MutationObserver && !accessibilityObserver) {
      accessibilityObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              accessibilityRoots.add(node);
            }
          });
        });

        if (accessibilityRoots.size && !accessibilityUpdateTimer) {
          accessibilityUpdateTimer = window.setTimeout(
            flushAccessibilityRoots,
            100,
          );
        }
      });
      accessibilityObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  };

  // Delivery note: turn "5-12 business days" into real dates from today
  // (weekends skipped) for the visitor's own calendar.
  const addBusinessDays = (start, days) => {
    const result = new Date(start);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const weekday = result.getDay();
      if (weekday !== 0 && weekday !== 6) added += 1;
    }
    return result;
  };

  // Visitor country: IP-based cookie already set on the store (ig-location),
  // otherwise a one-off lookup (cached for the session).
  const COUNTRY_CACHE_KEY = "casavelluti:visitor-country";
  let visitorCountryPromise = null;

  const getVisitorCountryCode = () => {
    if (visitorCountryPromise) return visitorCountryPromise;

    visitorCountryPromise = (async () => {
      const isCode = (value) => /^[A-Z]{2}$/.test(value || "");

      const cookie = document.cookie.match(/(?:^|;\s*)ig-location=([^;]*)/);
      if (cookie) {
        try {
          const code = (
            JSON.parse(decodeURIComponent(cookie[1])).country || ""
          ).toUpperCase();
          if (isCode(code)) return code;
        } catch (error) {
          /* fall through to lookup */
        }
      }

      try {
        const cached = sessionStorage.getItem(COUNTRY_CACHE_KEY);
        if (isCode(cached)) return cached;
      } catch (error) {
        /* storage unavailable */
      }

      try {
        const response = await fetch("https://www.cloudflare.com/cdn-cgi/trace");
        const code = ((await response.text()).match(/loc=([A-Z]{2})/) || [])[1];
        if (isCode(code) && code !== "XX" && code !== "T1") {
          try {
            sessionStorage.setItem(COUNTRY_CACHE_KEY, code);
          } catch (error) {
            /* storage unavailable */
          }
          return code;
        }
      } catch (error) {
        /* no country: destination stays hidden */
      }

      return "";
    })();

    return visitorCountryPromise;
  };

  const addDeliveryDestination = async (note) => {
    if (!note.hasAttribute("data-show-destination")) return;
    if (note.querySelector("[data-delivery-destination]")) return;

    const code = await getVisitorCountryCode();
    const text = note.querySelector(".cv-delivery-note__text");
    if (!code || !text || note.querySelector("[data-delivery-destination]")) {
      return;
    }

    let name = code;
    try {
      name = new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code;
    } catch (error) {
      /* keep the code as name */
    }

    const wrap = document.createElement("span");
    wrap.dataset.deliveryDestination = "";

    const sep = document.createElement("span");
    sep.className = "cv-delivery-note__sep";
    sep.setAttribute("aria-hidden", "true");
    sep.textContent = "\u2022";

    const strong = document.createElement("strong");
    strong.textContent = name;

    const flag = document.createElement("img");
    flag.className = "cv-delivery-note__flag";
    flag.src = `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
    flag.srcset = `https://flagcdn.com/w80/${code.toLowerCase()}.png 2x`;
    flag.width = 20;
    flag.height = 15;
    flag.alt = "";
    flag.loading = "lazy";

    wrap.append(
      sep,
      ` ${note.dataset.shipsLabel || "Ships to"} `,
      strong,
      flag,
    );
    text.append(" ", wrap);
  };

  const initDeliveryNotes = (root = document) => {
    root.querySelectorAll?.("[data-delivery-note]").forEach((note) => {
      const min = parseInt(note.dataset.minDays, 10);
      const max = parseInt(note.dataset.maxDays, 10);
      const lead = note.querySelector("[data-delivery-lead]");
      if (!lead || !min || !max || max < min) return;

      const today = new Date();
      const from = addBusinessDays(today, min);
      const to = addBusinessDays(today, max);
      const month = (date) =>
        date.toLocaleString("en-US", { month: "short" });
      const range =
        from.getMonth() === to.getMonth()
          ? `${month(from)} ${from.getDate()} – ${to.getDate()}`
          : `${month(from)} ${from.getDate()} – ${month(to)} ${to.getDate()}`;

      lead.textContent = "";
      lead.append(`${note.dataset.leadText || "Get it by"} `);
      const strong = document.createElement("strong");
      strong.textContent = range;
      lead.append(strong);

      addDeliveryDestination(note);
    });
  };

  // Run on DOM loaded
  document.addEventListener("DOMContentLoaded", () => {
    initDeliveryNotes();
    initEventListeners();
    updateUI();
    loadWishlistPage();
    initAccessibilityFixes();
    initSmartStickyAtc();
    initMobileZoomActions();
  });

  // Re-run wishlist page checks on Shopify Section editor load event
  document.addEventListener("shopify:section:load", (e) => {
    if (e.target.querySelector(".wishlist-page-section")) {
      loadWishlistPage();
    }

    updateUI();
    initDeliveryNotes(e.target);
    initAccessibilityFixes();
    initSmartStickyAtc(e.target);
    initMobileZoomActions();
  });

  document.addEventListener("shopify:section:unload", (e) => {
    e.target
      .querySelectorAll?.(SMART_STICKY_ATC_SELECTOR)
      .forEach((container) => {
        container.smartStickyInstance?.destroy();
      });
  });

  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.
})();
